import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, combineLatest, of } from 'rxjs';
import { mergeMap, take } from 'rxjs/operators';

import { AppConfig } from '../../../app.settings';
import { PlaylistDTO } from '../../models/view-models/playlist-models/playlist-dto.model';
import { DisplayPlaylistDTO } from '../../models/view-models/playlist-models/display-playlist-dto.model';
import { PlaylistDTOModel } from '../../models/view-models/playlist-models/playlist-dto-crud.model';
import { PlaylistItemDTO } from '../../models/view-models/playlist-item-models/playlist-item-dto.model';
import { ILauncherFeature } from '../../interfaces/launcher-feature.interface';
import { LaunchersService } from './launchers.service';
import { RssTickerConfigs } from '../../models/entities/rss-ticker.type';
import { PlaylistItemLinkModel } from '../../models/view-models/playlist-item-models/playlist-item-link.model';
import { PlaylistItemBorderSettings } from '../../models/view-models/playlist-item-models/playlist-item-border-settings.model';

@Injectable()
export class PlaylistsService {
  public playlistCollectionChanged = new BehaviorSubject<PlaylistDTO[]>([]);

  endpoint: string;
  titleState = new Subject();

  constructor(public http: HttpClient, public config: AppConfig, public launchersService: LaunchersService) {
    this.endpoint = this.config.getConfig('api') + 'Playlist';
   }

  getAllPlaylists(): Observable<PlaylistDTO[]> {
    const urlFull = this.endpoint;
    return this.http.get<PlaylistDTO[]>(urlFull, {headers: new HttpHeaders({ 'Content-Type': 'application/json' })});
  }

  createPlaylist(body): Observable<DisplayPlaylistDTO> {
    return this.http.post<DisplayPlaylistDTO>(this.endpoint, body,
       {headers: new HttpHeaders({ 'Content-Type': 'application/json' })});
  }

  updatePlaylist(body) {
    return this.http.put(this.endpoint, body,
      {headers: new HttpHeaders({ 'Content-Type': 'application/json' })});
  }

  deletePlaylist(id: number) {
    const urlFull = this.endpoint + '/' + id;
      return this.http.delete(urlFull,
        {headers: new HttpHeaders({ 'Content-Type': 'application/json' })});
  }

  getOnePlaylist(id: number): Observable<DisplayPlaylistDTO> {
    const urlFull = this.endpoint + '/' + id;
    return this.http.get<DisplayPlaylistDTO>(urlFull, {headers: new HttpHeaders({ 'Content-Type': 'application/json' })});
  }

  copyPlaylist(playlistId: number): Observable<[DisplayPlaylistDTO, DisplayPlaylistDTO]> {
    return this.getOnePlaylist(playlistId)
      .pipe(
        mergeMap(sourcePlaylist => {
          const duplicatedPlaylist: PlaylistDTOModel = {
            ...sourcePlaylist,
            PlaylistTitle: `${ sourcePlaylist.PlaylistTitle } (Copy)`,
            PlaylistItems: sourcePlaylist.PlaylistItems.map(
              ({Id, ...remainingAttrs}) => {
                const { Id: rssId, PlaylistItemId, ...configs } = { ...remainingAttrs.RssConfigs };
                remainingAttrs.RssConfigs = remainingAttrs.RssConfigs ? configs as RssTickerConfigs : null;

                const { Id: borderId, PlaylistItemId: itemId, ...borderSettings } = { ...remainingAttrs.BordersSettings };
                remainingAttrs.BordersSettings = remainingAttrs.BordersSettings ? borderSettings as PlaylistItemBorderSettings : null;

                const copiedItemLinks: PlaylistItemLinkModel[] = [];
                remainingAttrs.PlaylistItemLinks.forEach(source => {
                  const { Id, PlaylistItemId, ...model } = { ...source };
                  copiedItemLinks.push(model as PlaylistItemLinkModel);
                });
                remainingAttrs.PlaylistItemLinks = copiedItemLinks;

                return remainingAttrs as PlaylistItemDTO;
              }
            ),
            UsersWhoHaveAccessIds: sourcePlaylist.IsShared ? sourcePlaylist?.UsersWhoHaveAccess.map(
              (user) => user.Id): [],
          };

          return combineLatest([
            this.createPlaylist(duplicatedPlaylist),
            of(sourcePlaylist)
          ]);
        })
      );
  }

  setFeaturesToLauncher(createdPlaylist: DisplayPlaylistDTO, sourcePlaylist: DisplayPlaylistDTO): void {
    sourcePlaylist.PlaylistItems.forEach((playlistItem: PlaylistItemDTO) => {
      const createdPlylistItem: PlaylistItemDTO = createdPlaylist.PlaylistItems.find(
        (createdPlaylistItem) =>
          (createdPlaylistItem.IsLocal ?
          (createdPlaylistItem.LocalLauncher === playlistItem.LocalLauncher) :
          createdPlaylistItem.PlaylistItemLinks[0].Link === playlistItem.PlaylistItemLinks[0].Link) &&
          createdPlaylistItem.PlaylistItemTitle === playlistItem.PlaylistItemTitle
      );

      if (
        createdPlylistItem &&
        playlistItem.Launcher?.LauncherFeatures &&
        playlistItem.Launcher?.LauncherFeatures.length
      ) {
        const platlistItemId: number = createdPlylistItem.Id;

        playlistItem.Launcher.LauncherFeatures.forEach((launcherFeature: ILauncherFeature) => {
          launcherFeature.Id = 1;
          launcherFeature.PlaylistItemId = platlistItemId;

          this.launchersService.setFeaturesToLauncher(launcherFeature)
            .pipe(take(1))
            .subscribe();
        });
      }
    });
  }
}
